import { cloneDeep } from 'lodash-es';

// const url_path = window.location.pathname;

const state = {
  reloading: false,
  loaded: false,
  errored: false,
  errorType: null,
  errorData: null,
  options: {},
  preselectedFilters: [],
  search: {
    query: '',
    filters: [],
    page: 0,
    sortOption: null,
    extended: {},
  },
  results: {},
  previousItemResults: [],
};

const getters = {
  loaded: ({ loaded }) => loaded,
  errored: ({ errored }) => errored,
  errorType: ({ errorType }) => errorType,
  errorData: ({ errorData }) => errorData,
  reloading: ({ reloading }) => reloading,

  results: ({ results }) => results,
  pageLength: ({ options }) => options.pageLength,
  query: ({ search }) => search.query,
  sort: ({ search }) => search.sortOption,
  filters: ({ search }) => search.filters,
  currentPage: ({ search }) => search.page,
  extended: ({ search }) => search.extended,
};

const mutations = {
  setLoaded(state) {
    state.loaded = true;
    state.reloading = false;
  },
  setReloading(state) {
    state.reloading = true;
  },
  setErrored(state, { type, data }) {
    state.errored = true;
    state.errorType = type;
    state.errorData = data;
  },
  clearError(state) {
    state.errored = false;
    state.errorType = null;
    state.errorData = null;
  },
  updateQueryParams(state) {
    const url = new URL(window.location.href);
    const search_params = new URLSearchParams(url.search);
    Object.entries(state.search).forEach(([key, value]) => {
      if (key === 'extended') return;
      else if (Array.isArray(value)) value = createFilterString(value);
      else if (typeof value === 'object') value = false;
      // to do, this is for sort, would require a BE change to accept a sort id or something
      else if (typeof value === 'string') value = value.trim();

      if (key === 'page') {
        if (state.options.preserveResults) search_params.delete(key);
        else if (value) {
          search_params.set(state.options.queryStringKeyPage, value + 1);
        } else {
          search_params.delete(state.options.queryStringKeyPage);
        }
      } else if (key === 'query') {
        if (value) {
          search_params.set(state.options.queryStringKeyQuery, value);
        } else search_params.delete(state.options.queryStringKeyQuery);
      } else if (key === 'filters') {
        if (value) {
          search_params.set(state.options.queryStringKeyFilters, value);
        } else search_params.delete(state.options.queryStringKeyFilters);
      } else if (value) {
        search_params.set(key, value);
      } else search_params.delete(key);
    });

    const paramsExist = !search_params.keys().next().done;
    const newURL = `${url.pathname}${
      paramsExist ? `?${search_params.toString()}` : ''
    }`;
    const historyState = state.options.paginationUsesHistory
      ? { page: state.search.page }
      : {};
    history.replaceState(historyState, '', decodeURIComponent(newURL));
  },
  applyOptions(state, payload) {
    state.options = payload;
  },
  parseInitialQueryParams(state) {
    const url = new URL(window.location.href);
    const search_params = new URLSearchParams(url.search);
    search_params.forEach((value, key) => {
      if (!value) return;
      else if (key === state.options.queryStringKeyFilters) {
        state.preselectedFilters = value.split(',');
      } else if (key === state.options.queryStringKeyPage) {
        if (!state.options.preserveResults) {
          if (!isNaN(value)) {
            let page = Number(value);
            if (page > 0) page--;
            state.search.page = page;
          }
        }
      } else if (key === state.options.queryStringKeyQuery) {
        state.search.query = value;
      }
    });
  },
  setResults(state, payload) {
    if (state.options.preserveResults) {
      state.previousItemResults.push(...payload.itemResults);
      payload.itemResults = state.previousItemResults;
    }
    state.results = payload;
    state.search.filters = cloneDeep(payload.filters);
    state.search.query = payload.queryUsed.searchTerm;
    state.search.sortOption = payload.queryUsed.sortOption;
    state.search.page = payload.queryUsed.currentPage;
    state.search.extended = payload.extended
      ? cloneDeep(payload.extended)
      : payload.queryUsed.extended;
    state.preselectedFilters = [];
  },
  setQuery(state, payload) {
    state.search.query = payload;
    state.search.page = 0;
  },
  resetAllFilters(state) {
    const groups = state.search.filters;
    state.search.page = 0;
    const recurse = filters => {
      return filters.forEach(f => {
        f.selected = false;
        if (f.filters && f.filters.length) recurse(f.filters);
      });
    };
    groups.forEach(g => recurse(g.filters));
  },
  setFilterValue(state, { filter, value }) {
    const groups = state.search.filters;
    state.search.page = 0;
    const recurse = filters => {
      return filters.some(f => {
        if (f.id === filter.id) return (f.selected = value), true;
        if (f.filters && f.filters.length) return recurse(f.filters);
        return false;
      });
    };
    groups.some(g => {
      return recurse(g.filters);
    });
  },
  setPageIndex(state, payload) {
    state.search.page = payload;
  },
  setSortOrder(state, payload) {
    state.search.sortOption = payload;
    state.search.page = 0;
  },
  clearPreviousItemResults(state) {
    state.previousItemResults = [];
  },
  extend(state, callback) {
    state.search.extended = cloneDeep(callback(state.search.extended));
  },
  clearSearchParameters(state) {
    state.loaded = false;
    state.preselectedFilters = [];
    state.search.query = '';
    state.search.filters = [];
    state.search.page = 0;
    state.search.sortOption = null;
    state.search.extended = {};
    state.results = {};
    state.previousItemResults = [];
  },
  checkForConfigErrors(state) {
    if (state.options.queryRequired) {
      if (state.search.query.trim().length < 1) {
        this.commit('setErrored', { type: 'queryRequired', data: null });
      } else this.commit('clearError');
    }
  },
};

const actions = {
  changeSort({ commit, dispatch }, sort) {
    commit('setSortOrder', sort);
    commit('clearPreviousItemResults');
    return dispatch('executeSearch');
  },
  goToPage({ commit, dispatch }, index) {
    commit('setPageIndex', index);
    return dispatch('executeSearch');
  },
  removeAllTags({ commit, dispatch }) {
    commit('resetAllFilters');
    commit('clearPreviousItemResults');
    return dispatch('executeSearch');
  },
  removeTagItem({ commit, dispatch }, tag) {
    commit('setFilterValue', { filter: tag, value: false });
    commit('clearPreviousItemResults');
    return dispatch('executeSearch');
  },
  resetSearch({ commit, dispatch }, callback = q => q) {
    commit('clearSearchParameters');
    dispatch('executeSearch', callback);
  },
  executeSearch({ commit, state }, callback = q => q) {
    return new Promise(async resolve => {
      commit('checkForConfigErrors');
      if (state.errored) return resolve();

      const start = new Date().getTime();
      commit('setReloading');
      let transformed;
      let data = {
        searchIndex: state.options.searchIndex,
        searchTerm: state.search.query,
        filters: state.search.filters,
        currentPage: state.search.page,
        pageSize: state.options.pageLength,
        preselectedFilters: state.preselectedFilters,
        extended: state.search.extended,
        ...(state.search.sortOption && {
          sortOption: state.search.sortOption,
        }),
      };
      data.searchTerm = String(data.searchTerm);

      if (callback && typeof callback === 'function')
        transformed = callback(data);
      if (transformed) data = cloneDeep(transformed);
      data = JSON.stringify(data);

      const response = await fetch(state.options.endpoint, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        body: data,
      });
      if (response.ok) {
        const results = await response.json();
        commit('setResults', results);
        commit('updateQueryParams');
        const elapsed = new Date().getTime() - start;
        const timeout = elapsed < 750 ? 750 - elapsed : 0;
        setTimeout(() => {
          commit('setLoaded');
          resolve();
        }, timeout);
      } else {
        const error = await response.json();
        commit('setErrored', { type: 'severError', data: error });
        resolve();
      }
    });
  },
};

// Create a new store instance.
const store = {
  state,
  getters,
  mutations,
  actions,
};

export default store;

function createFilterString(filters) {
  const ids = [];
  const recurse = filters => {
    return filters.reduce((_selections, f) => {
      if (f.selected == true) _selections.push(f.id);
      if (f.filters && f.filters.length) {
        let childSelections = recurse(f.filters);
        if (childSelections.length) _selections.push(...childSelections);
      }
      return _selections;
    }, []);
  };
  filters.forEach(g => {
    let selections = recurse(g.filters);
    if (selections.length) {
      ids.push(...selections);
    }
  });
  return ids.join(',');
}
